import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi2";
import { SparklesIcon } from "@heroicons/react/24/solid";
import {
  FlagIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { userService } from "../../services/userService";
import { UserData } from "../../types/user";
import Header from "../../components/layout/Header";
import ComingSoon from "../../components/ComingSoon";
import { useSubscription } from "../../providers/SubscriptionProvider";

const DEFAULTS = {
  sleepGoal: 8,
  loadGoal: 400,
  weeklyLoadGoal: 2800,
};

/**
 * Goals (Settings.md / PerformanceLog.md §5.3): a single text area bound to
 * `users/{uid}.performanceGoals`, plus the daily targets. Events are created
 * and edited on the Schedule.
 */
const GoalsSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const { isPlusOrHigher } = useSubscription();
  const navigate = useNavigate();
  const uid = currentUser?.uid;

  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);

  // Goals text
  const [performanceGoals, setPerformanceGoals] = useState<string>("");
  const [savedGoals, setSavedGoals] = useState<string>("");

  // Daily targets
  const [sleepGoal, setSleepGoal] = useState<string>(DEFAULTS.sleepGoal.toString());
  const [loadGoal, setLoadGoal] = useState<string>(DEFAULTS.loadGoal.toString());
  const [weeklyLoadGoal, setWeeklyLoadGoal] = useState<string>(
    DEFAULTS.weeklyLoadGoal.toString(),
  );

  useEffect(() => {
    const fetch = async () => {
      if (!uid) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const { userData } = await userService.getUserProfile(uid);
        if (userData) {
          setUserData(userData);
          const u: any = userData;
          const goalsText = typeof u.performanceGoals === "string" ? u.performanceGoals : "";
          setPerformanceGoals(goalsText);
          setSavedGoals(goalsText);
          setSleepGoal(
            typeof u.sleepGoal === "number" && u.sleepGoal > 0
              ? u.sleepGoal.toString()
              : DEFAULTS.sleepGoal.toString(),
          );
          setLoadGoal(
            typeof u.loadGoal === "number" && u.loadGoal >= 0
              ? u.loadGoal.toString()
              : DEFAULTS.loadGoal.toString(),
          );
          setWeeklyLoadGoal(
            typeof u.weeklyLoadGoal === "number" && u.weeklyLoadGoal >= 0
              ? u.weeklyLoadGoal.toString()
              : DEFAULTS.weeklyLoadGoal.toString(),
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [uid]);

  const save = useCallback(
    async (updates: Partial<UserData>) => {
      if (!uid) return;
      setSaving(true);
      setError(null);
      try {
        await userService.updateUserProfile(uid, updates);
        setSavedAt(Date.now());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save settings");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [uid],
  );

  const saveGoalsText = async () => {
    const next = performanceGoals.trim();
    if (next === savedGoals.trim()) return;
    await save({ performanceGoals: next }).catch(() => {});
    setSavedGoals(next);
  };

  const saveDailyTargets = async () => {
    const validated = {
      sleepGoal: Math.max(1, Math.min(24, parseFloat(sleepGoal) || DEFAULTS.sleepGoal)),
      loadGoal: Math.max(0, Math.round(parseInt(loadGoal) || DEFAULTS.loadGoal)),
      weeklyLoadGoal: Math.max(
        0,
        Math.round(parseInt(weeklyLoadGoal) || DEFAULTS.weeklyLoadGoal),
      ),
    };
    await save(validated).catch(() => {});
  };

  if (loading) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <div className="card shadow-lg p-8 mt-8 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Error</h3>
          <p className="text-base-content/70 mb-6">{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const cardClass = "bg-base-100 rounded-2xl shadow-sm border border-base-300 p-6 mb-4";

  return (
    <div className="w-full pt-16">
      <Header />

      <div className="container max-w-2xl mx-auto py-8 px-6">
        {/* Page header */}
        <div className="flex items-center justify-between pb-2">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors mr-4"
            >
              <HiArrowLeft className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content">Goals</h1>
          </div>
          <button
            onClick={() => setShowInfoDialog(true)}
            className="p-1 text-base-content/60 hover:text-base-content/80 transition-colors"
            aria-label="Goals Information"
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Save status */}
        <div className="flex items-center justify-end h-6 mb-2 text-sm">
          {saving ? (
            <span className="flex items-center gap-2 text-base-content/60">
              <span className="loading loading-spinner loading-xs"></span>
              Saving...
            </span>
          ) : error ? (
            <span className="text-error">{error}</span>
          ) : savedAt ? (
            <span className="text-success">All changes saved</span>
          ) : null}
        </div>

        {/* ── 1. Goals ──────────────────────────────────────────────────── */}
        <div className={`${cardClass} relative`}>
          {!isPlusOrHigher && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl">
              <ComingSoon featureKey="performanceGoalSetting" />
            </div>
          )}
          <div className={!isPlusOrHigher ? "blur-[1px] pointer-events-none opacity-60" : ""}>
            <div className="flex items-center gap-2 mb-1">
              <FlagIcon className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold text-base-content">Goals</h3>
            </div>
            <p className="mb-4 text-base-content/70 text-sm">
              What are you working towards? Write it in your own words - a target
              time, a championship, a season outcome. This guides your AI insights.
            </p>
            <textarea
              value={performanceGoals}
              onChange={(e) => setPerformanceGoals(e.target.value)}
              onBlur={saveGoalsText}
              placeholder="e.g. Run a sub 18:00 5k by August and qualify for the state championships."
              aria-label="Goals"
              disabled={!isPlusOrHigher}
              className="w-full resize-none rounded-md p-4 placeholder:text-base-content/60 bg-base-300 focus:outline-none text-[16px]"
              rows={6}
            />
          </div>
        </div>

        {/* ── 2. Daily targets ──────────────────────────────────────────── */}
        <div className={cardClass}>
          <h3 className="text-lg font-semibold text-base-content mb-1">Daily targets</h3>
          <p className="mb-4 text-base-content/70 text-sm">
            Set the daily targets you'd like to see on your dashboard.
          </p>
          <div className="space-y-3">
            <div className="new-form-group-rounded w-full">
              <input
                type="number"
                min={1}
                max={24}
                step={0.5}
                className="form-element-rounded"
                value={sleepGoal}
                onChange={(e) => setSleepGoal(e.target.value)}
                onBlur={saveDailyTargets}
                placeholder="e.g. 7.5"
              />
              <label>Sleep goal (hours)</label>
            </div>
            <div className="new-form-group-rounded w-full">
              <input
                type="number"
                min={0}
                className="form-element-rounded"
                value={loadGoal}
                onChange={(e) => setLoadGoal(e.target.value)}
                onBlur={saveDailyTargets}
                placeholder="e.g. 400"
              />
              <label>Daily load goal</label>
            </div>
            <div className="new-form-group-rounded w-full">
              <input
                type="number"
                min={0}
                className="form-element-rounded"
                value={weeklyLoadGoal}
                onChange={(e) => setWeeklyLoadGoal(e.target.value)}
                onBlur={saveDailyTargets}
                placeholder="e.g. 2800"
              />
              <label>Weekly load goal</label>
            </div>
          </div>
        </div>

        {/* Information Dialog */}
        {showInfoDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-grotesk font-semibold text-base-content">
                    <span className="font-light">The purpose of</span> Goals
                  </h3>
                  <button
                    onClick={() => setShowInfoDialog(false)}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>
                <div className="space-y-4 text-base-content/80">
                  <p className="font-grotesk text-lg">
                    Your goals are a short note on what you are working towards.
                    Your coach (if you have one) keeps their own private goals.
                  </p>
                  <div>
                    <h4 className="font-semibold text-base-content mb-2 flex items-center gap-2">
                      <FlagIcon className="h-4 w-4" />
                      Goals
                    </h4>
                    <p className="text-sm">
                      Free text, in your own words. It guides the AI insights with
                      personalised recommendations. Competitions, game days and
                      testing are scheduled as events on your Schedule.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-base-content mb-2 flex items-center gap-2">
                      Daily targets
                      <SparklesIcon className="h-4 w-4 text-base-content/80" />
                    </h4>
                    <p className="text-sm">
                      Set your target sleep hours and training load so your
                      dashboard shows how you're progressing each day.
                    </p>
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    onClick={() => setShowInfoDialog(false)}
                    className="w-full bg-neutral text-white font-grotesk px-4 py-2 rounded-md hover:bg-neutral/90 transition-colors"
                  >
                    Got it
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalsSettings;
